import { useEffect, useState, useRef, useCallback } from "react";
import { ArrowLeft, Camera, Calendar, Clock, MapPin, DollarSign, Star, CheckCircle, Car, Phone, MessageCircle, ChevronDown, Share2, Upload, Download, Image, Video } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useAuth } from "@/contexts/AuthContext";
import type { Booking } from "@/types";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { invokeFunctionWithAuth } from "@/lib/supabase/functions";
import { getStepFromStatus, getStepInfo, shouldShowFindingFilmmaker, canCustomerSeeFilmmaker } from "@/utils/booking-steps";
import { StepProgressBar } from "@/components/tracking/StepProgressBar";
import { loadGoogleMapsLibrary } from "@/lib/google-maps-loader";
import { useBookingRealtime } from "@/hooks/useBookingRealtime";
import { useFilmmakerLocationRealtime } from "@/hooks/useFilmmakerLocationRealtime";
import { useRouteCalculation } from "@/hooks/useRouteCalculation";
import { DynamicMap } from "@/components/tracking/DynamicMap";

export default function TrackBooking() {
  const navigate = useNavigate();
  const { bookingId } = useParams<{ bookingId: string }>();
  const { user } = useAuth();
  const [booking, setBooking] = useState<Booking | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [whatsNextOpen, setWhatsNextOpen] = useState(true);
  const [estimatedMinutes, setEstimatedMinutes] = useState(12);
  const [progress, setProgress] = useState(40);
  const [elapsedMinutes, setElapsedMinutes] = useState(0);
  const [selectedTip, setSelectedTip] = useState<number | null>(null);
  const [verificationCode, setVerificationCode] = useState<string[]>(['', '', '', '', '', '']);
  const [verificationError, setVerificationError] = useState<string>('');
  const mapRef = useRef<HTMLDivElement>(null);
  const googleMapRef = useRef<google.maps.Map | null>(null);
  const loadedRef = useRef<string | null>(null);
  const sessionStartTimeRef = useRef<number | null>(null);
  const updatingStatusRef = useRef(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const isSubmittingRef = useRef(false);
  const [filmmakerLocation, setFilmmakerLocation] = useState<{ lat: number; lng: number } | null>(null);

  useEffect(() => {
    // Reset if bookingId changed
    if (loadedRef.current !== bookingId) {
      loadedRef.current = null;
      setBooking(null);
      setLoading(true);
    }
    
    // Prevent multiple executions for same bookingId
    if (loadedRef.current === bookingId) {
      return;
    }
    
    const loadBooking = async () => {
      if (!bookingId) {
        setLoading(false);
        return;
      }
      
      loadedRef.current = bookingId;
      setLoading(true);
      
      // Fetch booking using edge function (bypasses RLS issues)
      try {
        // Get userId - try from user context, fallback to localStorage
        let userId = user?.id;
        if (!userId) {
          const authSessionStr = localStorage.getItem('auth_session');
          if (authSessionStr) {
            try {
              const authSession = JSON.parse(authSessionStr);
              userId = authSession.userId || authSession.user?.id;
            } catch (e) {
              console.warn('Error parsing auth session:', e);
            }
          }
          
          // If still no userId, wait a bit for user context to initialize
          if (!userId) {
            // Reset loaded flag to allow retry when user is available
            loadedRef.current = null;
            setLoading(false);
            return;
          }
        }

        if (!userId) {
          console.error('No userId available - cannot load booking');
          toast.error('You must be logged in to track bookings');
          navigate('/auth/login');
          setLoading(false);
          return;
        }
        const { data: bookingResponse, error: fetchError } = await invokeFunctionWithAuth('get-booking-details', {
          bookingId: bookingId, // Pass as-is, function handles BK-XXX format
          userId: userId,
          phone: user?.phone
        });

        if (fetchError) {
          console.error('❌ Error fetching booking:', fetchError);
          console.error('Error details:', JSON.stringify(fetchError, null, 2));
          
          // Handle specific error cases
          if (fetchError.status === 403) {
            toast.error('You do not have permission to view this booking');
            navigate('/dashboard');
          } else if (fetchError.status === 404) {
            toast.error('Booking not found');
            navigate('/dashboard');
          } else {
            toast.error('Failed to load booking details');
          }
          setLoading(false);
          return;
        }

        if (bookingResponse?.booking) {
          setBooking(bookingResponse.booking as any);
          setLoading(false);
          return;
        }

        // If we get here, booking not found
        console.warn('⚠️ No booking in response:', bookingResponse);
        console.warn('Response keys:', bookingResponse ? Object.keys(bookingResponse) : 'null');
        toast.error('Booking not found');
        navigate('/dashboard');
        setLoading(false);
      } catch (error: any) {
        console.error('💥 Exception loading booking:', error);
        console.error('Error stack:', error.stack);
        toast.error('Failed to load booking details');
        setLoading(false);
        // Reset loaded flag on error so user can retry
        if (loadedRef.current === bookingId) {
          loadedRef.current = null;
        }
      }
    };
    
    loadBooking();
  }, [bookingId, user]);

  // Redirect to FindingFilmmaker if status is paid or assigned
  useEffect(() => {
    if (booking && shouldShowFindingFilmmaker(booking.status)) {
      navigate(`/track/${bookingId}/finding`, { replace: true });
    }
  }, [booking, bookingId, navigate]);

  // Assign filmmaker to booking if not assigned
  useEffect(() => {
    const assignFilmmaker = async () => {
      if (!booking || booking.filmmaker_id) return;

      try {
        // Find an active filmmaker for the booking's location
        const { data: filmmakers, error } = await supabase
          .from('filmmakers')
          .select(`
            id,
            location_id,
            bio,
            rating,
            is_active,
            profiles!inner(id, full_name, email, phone, avatar_url)
          `)
          .eq('location_id', booking.location_id)
          .eq('is_active', true)
          .limit(1);

        if (error) {
          console.error('Error fetching filmmakers:', error);
          return;
        }

        if (filmmakers && filmmakers.length > 0) {
          const selectedFilmmaker = filmmakers[0];
          
          // Update booking with filmmaker_id
          const { error: updateError } = await supabase
            .from('bookings')
            .update({ filmmaker_id: selectedFilmmaker.id })
            .eq('id', booking.id);

          if (updateError) {
            console.error('Error assigning filmmaker:', updateError);
            return;
          }

          // Update local booking state with filmmaker data
          // Handle different response structures from PostgREST
          let profile;
          if (Array.isArray(selectedFilmmaker.profiles)) {
            profile = selectedFilmmaker.profiles[0];
          } else if (selectedFilmmaker.profiles) {
            profile = selectedFilmmaker.profiles;
          } else {
            // If profile is missing, try to fetch it separately
            const { data: profileData, error: profileError } = await supabase
              .from('profiles')
              .select('full_name, email, phone, avatar_url')
              .eq('id', selectedFilmmaker.id)
              .single();
            
            if (profileError || !profileData) {
              console.error('Error fetching profile separately:', profileError);
              return;
            }
            profile = profileData;
          }
          
          // Ensure profile exists and has required fields
          if (!profile) {
            console.error('Filmmaker profile not found');
            return;
          }
            
          setBooking({
            ...booking,
            filmmaker_id: selectedFilmmaker.id,
            filmmaker: {
              id: selectedFilmmaker.id,
              full_name: profile.full_name || 'Filmmaker',
              email: profile.email || '',
              phone: profile.phone || '',
              avatar_url: profile.avatar_url || null,
              location_id: selectedFilmmaker.location_id,
              bio: selectedFilmmaker.bio || '',
              rating: selectedFilmmaker.rating || 5.0,
              is_active: selectedFilmmaker.is_active ?? true,
            } as any
          });
        }
      } catch (error) {
        console.error('Error in assignFilmmaker:', error);
      }
    };

    assignFilmmaker();
  }, [booking]);

  // Helper to update booking status that works in both mock and production
  const updateBookingStatus = useCallback(async (newStatus: string) => {
    if (!booking || !user?.id) return;
    
    // Prevent multiple simultaneous calls
    if (updatingStatusRef.current) {
      console.log('⚠️ Update already in progress, skipping...');
      return;
    }

    // Update via edge function to bypass RLS
    updatingStatusRef.current = true;
    
    try {
      // Determine what to update based on status change
      const updateData: any = { status: newStatus };
      
      // Set session_started_at when moving to in_progress
      if (newStatus === 'in_progress' && booking.status !== 'in_progress') {
        updateData.session_started_at = new Date().toISOString();
      }
      
      // Set session_finished_at when leaving in_progress
      if (booking.status === 'in_progress' && newStatus !== 'in_progress') {
        updateData.session_finished_at = new Date().toISOString();
      }
      
      // Update local state immediately for better UX
      setBooking({ ...booking, status: newStatus as any, ...updateData });
      
      // Use edge function to update status (bypasses RLS)
      const { data, error } = await invokeFunctionWithAuth('update-booking-status', {
        bookingId: booking.id,
        userId: user.id,
        status: newStatus,
        session_started_at: updateData.session_started_at,
        session_finished_at: updateData.session_finished_at,
      });

      if (error) {
        console.error('Error updating booking status:', error);
        toast.error('Failed to update booking. Please try again.');
        // Revert local state on error
        setBooking(booking);
      } else {
        // Update with response data if available
        if (data?.booking) {
          setBooking(data.booking as Booking);
        }
      }
    } catch (error) {
      console.error('Error updating booking:', error);
      toast.error('Failed to update status');
      // Revert local state on error
      setBooking(booking);
    } finally {
      updatingStatusRef.current = false;
    }
  }, [booking, user, bookingId]);

  // Função chamada quando Realtime detecta mudança
  const handleRealtimeUpdate = useCallback(async (updatedBooking: Booking | null) => {
    if (!bookingId || !user) return;

    // Se receber null, significa que precisa recarregar do servidor
    if (updatedBooking === null) {
      console.log('🔄 Realtime detected change in TrackBooking - reloading booking...');
      
      let userId = user.id;
      if (!userId) {
        const authSessionStr = localStorage.getItem('auth_session');
        if (authSessionStr) {
          try {
            const authSession = JSON.parse(authSessionStr);
            userId = authSession.userId || authSession.user?.id;
          } catch (e) {
            console.warn('Error parsing auth session:', e);
          }
        }
      }

      if (userId) {
        try {
          const bookingIdShort = bookingId?.startsWith('BK-') ? bookingId : `BK-${bookingId?.substring(0, 8).toUpperCase()}`;
          const { data: bookingResponse, error } = await invokeFunctionWithAuth('get-booking-details', {
            bookingId: bookingIdShort,
            userId: userId,
            phone: user.phone
          });

          if (!error && bookingResponse?.booking) {
            const newBooking = bookingResponse.booking as Booking;
            setBooking(newBooking);
          }
        } catch (error) {
          console.error('Error reloading booking:', error);
        }
      }
      return;
    }

    // Mock mode ou booking recebida diretamente
    setBooking(updatedBooking);
  }, [bookingId, user]);

  // Usar hook Realtime para escutar mudanças
  useBookingRealtime({
    bookingId: booking?.id || bookingId, // Preferir booking.id (UUID real), fallback para bookingId do route
    onUpdate: handleRealtimeUpdate,
    enabled: !!booking && !loading, // Só habilitar quando booking estiver carregado
  });

  // Listen for filmmaker location updates via Realtime
  const latestLocation = useFilmmakerLocationRealtime({
    bookingId: booking?.id,
    enabled: booking?.status === 'en_route' && !!booking?.id,
    onLocationUpdate: (location) => {
      if (location) {
        setFilmmakerLocation({ lat: location.lat, lng: location.lng });
        // Update booking with current location
        if (booking) {
          setBooking({
            ...booking,
            filmmaker_current_location: {
              lat: location.lat,
              lng: location.lng,
              heading: location.heading,
              speed: location.speed,
              accuracy: location.accuracy,
              timestamp: location.location_timestamp,
            },
          });
        }
      } else {
        setFilmmakerLocation(null);
      }
    },
  });

  // Calculate route and ETA when we have both origin and destination
  const destination = booking ? {
    lat: booking.lat || 43.6532,
    lng: booking.lng || -79.3832,
  } : null;

  const { eta, etaMinutes, route, loading: routeLoading } = useRouteCalculation({
    origin: filmmakerLocation,
    destination: destination,
    enabled: booking?.status === 'en_route' && !!filmmakerLocation && !!destination,
    travelMode: 'DRIVING',
  });

  // Update estimated arrival in booking
  useEffect(() => {
    if (booking && eta && etaMinutes !== null) {
      // Only update if values actually changed to avoid infinite loops
      const currentEtaTime = booking.estimated_arrival_time;
      const currentEtaMinutes = booking.estimated_arrival_minutes;
      
      if (currentEtaTime !== eta.toISOString() || currentEtaMinutes !== etaMinutes) {
        setBooking({
          ...booking,
          estimated_arrival_time: eta.toISOString(),
          estimated_arrival_minutes: etaMinutes,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eta, etaMinutes]);

  // Simulate countdown for en_route status
  useEffect(() => {
    if (booking?.status !== 'en_route') return;

    const interval = setInterval(() => {
      setEstimatedMinutes((prev) => {
        const newMinutes = prev > 0 ? prev - 1 : 0;
        
        if (newMinutes === 0 && bookingId) {
          clearInterval(interval);
          setTimeout(async () => {
            await updateBookingStatus('arrived');
          }, 1000);
        }
        
        return newMinutes;
      });
      setProgress((prev) => (prev < 100 ? prev + 2 : 100));
    }, 30000);

    return () => clearInterval(interval);
  }, [booking?.status, bookingId, booking, user, updateBookingStatus]);

  // Calculate real shooting progress based on elapsed time
  useEffect(() => {
    if (booking?.status !== 'in_progress') {
      setElapsedMinutes(0);
      setProgress(0);
      sessionStartTimeRef.current = null; // Reset when not in progress
      return;
    }

    // If we don't have a session start time yet, this is when the session started
    // Prefer session_started_at (most accurate), fallback to updated_at, then current time
    if (!sessionStartTimeRef.current) {
      if (booking.session_started_at) {
        sessionStartTimeRef.current = new Date(booking.session_started_at).getTime();
      } else if (booking.updated_at) {
        sessionStartTimeRef.current = new Date(booking.updated_at).getTime();
      } else {
        sessionStartTimeRef.current = Date.now();
      }
    }
    
    const interval = setInterval(() => {
      const now = Date.now();
      const elapsedMillis = now - sessionStartTimeRef.current!;
      const elapsedMins = Math.floor(elapsedMillis / 60000);
      
      const totalMinutes = booking?.package?.duration_minutes || 180;
      const newProgress = Math.min((elapsedMins / totalMinutes) * 100, 100);
      
      setElapsedMinutes(elapsedMins);
      setProgress(newProgress);
    }, 1000); // Update every second for real-time accuracy

    return () => clearInterval(interval);
  }, [booking?.status, booking?.package?.duration_minutes, booking?.session_started_at, booking?.updated_at]);

  // Note: Upload progress removed - actual upload will be controlled by filmmaker
  // When filmmaker marks content as uploaded, status will change to 'uploaded' or 'completed'

  // Handlers
  const handleVerifyIdentity = useCallback(async () => {
    if (!booking) return;
    
    // Get current code from state
    const code = verificationCode.join('');
    if (code.length !== 6 || !code.match(/^\d{6}$/)) {
      isSubmittingRef.current = false;
      return; // Don't show error, just don't proceed
    }

    setVerificationError('');
    
    // Update status via Edge Function with verification code
    const { data, error } = await invokeFunctionWithAuth('update-booking-status', {
      bookingId: booking.id,
      userId: user?.id,
      status: 'in_progress',
      verification_code: code,
    });

    if (error) {
      setVerificationError('Invalid code. Please try again.');
      // Clear code on error
      setVerificationCode(['', '', '', '', '', '']);
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
      isSubmittingRef.current = false;
    } else {
      // Success - status will be updated via Realtime
      toast.success('Identity confirmed! Shooting session started.');
      setVerificationCode(['', '', '', '', '', '']);
      isSubmittingRef.current = false;
    }
  }, [booking, verificationCode, user?.id]);

  // Auto-submit when code is complete
  useEffect(() => {
    const code = verificationCode.join('');
    if (code.length === 6 && code.match(/^\d{6}$/) && booking && booking.status === 'arrived' && !isSubmittingRef.current) {
      isSubmittingRef.current = true;
      // Small delay to ensure UI updates before submitting
      const timer = setTimeout(() => {
        handleVerifyIdentity();
        // Reset flag after a delay to allow for retry if needed
        setTimeout(() => {
          isSubmittingRef.current = false;
        }, 2000);
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [verificationCode, booking?.status, booking?.id, handleVerifyIdentity]);

  // Handle input change for verification code
  const handleCodeInput = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return; // Only allow digits
    
    const newCode = [...verificationCode];
    newCode[index] = value.slice(-1); // Only take last character
    setVerificationCode(newCode);
    setVerificationError('');

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // State will be updated, useEffect will handle auto-submit
  };

  // Handle backspace
  const handleCodeKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Handle paste
  const handleCodePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6).replace(/\D/g, '');
    if (pastedData.length === 6) {
      const newCode = pastedData.split('');
      setVerificationCode(newCode);
      setVerificationError('');
      // Focus last input
      inputRefs.current[5]?.focus();
      // Auto-submit
      setTimeout(() => handleVerifyIdentity(), 100);
    }
  };

  const handleMarkCompleted = async () => {
    if (!booking) return;
    // When customer marks as completed, status should already be done_waiting_upload
    // Update to awaiting_finish_confirm (filmmaker will then confirm to go to uploaded)
    await updateBookingStatus('awaiting_finish_confirm');
    toast.success('Service marked as completed! Waiting for filmmaker confirmation.');
  };

  const handleReview = async () => {
    if (!booking) return;
    
    // If status is uploaded, complete the booking when user clicks review
    if (booking.status === 'uploaded') {
      await updateBookingStatus('completed');
      toast.success('Thank you for your review!');
    } else {
      // For completed status, just show review modal/page (to be implemented)
      // TODO: Open review modal/page
    }
  };

  const handleCancelBooking = async () => {
    if (!booking) return;
    
    // Confirmação
    const confirmed = window.confirm(
      "Are you sure you want to cancel this booking? This action cannot be undone."
    );
    
    if (!confirmed) return;

    try {
      const { data, error } = await invokeFunctionWithAuth('cancel-booking', {
        bookingId: booking.id,
        cancelReason: 'Canceled by customer'
      });

      if (error) {
        console.error('Error canceling booking:', error);
        toast.error(error.message || 'Failed to cancel booking');
        return;
      }

      if (data?.success) {
        toast.success('Booking canceled successfully');
        // Limpar dados do sessionStorage para evitar criar novo booking acidentalmente
        sessionStorage.removeItem('bookingFormData');
        sessionStorage.removeItem('confirmedBooking');
        // Atualizar o booking local
        setBooking({ ...booking, status: 'canceled' });
        // Redirecionar para dashboard após alguns segundos
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      }
    } catch (error: any) {
      console.error('Error canceling booking:', error);
      toast.error('Failed to cancel booking. Please try again.');
    }
  };

  if (loading || !booking) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">{loading ? 'Loading booking...' : 'Booking not found'}</p>
      </div>
    );
  }

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  
  const formatTime = (dateStr: string | Date | undefined | null) => {
    if (!dateStr) return '';
    const date = typeof dateStr === 'string' ? new Date(dateStr) : dateStr;
    // Check if date is valid
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      return '';
    }
    // Use location timezone if available, otherwise use browser's local timezone
    const timezone = booking.location?.timezone;
    const options: Intl.DateTimeFormatOptions = { 
      hour: '2-digit', 
      minute: '2-digit',
      ...(timezone && { timeZone: timezone })
    };
    return date.toLocaleTimeString('en-US', options);
  };
  
  const formatPrice = (cents: number) => 
    `$${(cents / 100).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // Calculate real-time elapsed and remaining minutes for in_progress status
  const totalMinutes = booking.package?.duration_minutes || 180;
  
  // For in_progress, use real elapsed time calculated by useEffect; for other statuses, use 0
  const actualElapsedMinutes = booking.status === 'in_progress' ? elapsedMinutes : 0;
  const remainingMinutes = Math.max(totalMinutes - actualElapsedMinutes, 0);
  const hours = Math.floor(remainingMinutes / 60);
  const minutes = remainingMinutes % 60;
  
  // Calculate estimated end time based on session start + package duration
  // Prefer session_started_at if available (most accurate), fallback to scheduled_at
  const getValidDate = (dateStr: string | undefined | null): Date | null => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? null : date;
  };
  
  const sessionStartTime = getValidDate(booking.session_started_at) || getValidDate(booking.scheduled_at) || new Date();
  const estimatedEndTime = sessionStartTime ? new Date(sessionStartTime.getTime() + totalMinutes * 60000) : new Date();
  const uploadEndTime = estimatedEndTime ? new Date(estimatedEndTime.getTime() + 30 * 60000) : new Date();

  const tipOptions = [
    { label: '10%', value: 10 },
    { label: '15%', value: 15 },
    { label: '20%', value: 20 },
  ];

  // Render based on booking status
  const renderContent = () => {
    switch (booking.status) {
      case 'en_route':
        return (
          <>
            {/* En Route - Filmmaker Coming */}
            <Card className="p-5 space-y-4">
              <div className="flex gap-3">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Car className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h2 className="font-bold text-lg mb-1">
                    Your Filmmaker is Coming to You and Should Arrive Shortly
                  </h2>
                  <p className="text-sm text-muted-foreground">Step 2 of 4</p>
                  <StepProgressBar currentStep={2} totalSteps={4} className="h-2 mt-2" />
                  <p className="text-xs text-muted-foreground mt-2">
                    {booking.filmmaker?.full_name} has confirmed your shooting.
                  </p>
                </div>
              </div>
            </Card>

            <div className="space-y-3">
              <h2 className="text-2xl font-bold">{booking.filmmaker?.full_name} is on the Way</h2>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Estimated Arrival in</span>
                <span className="text-2xl font-bold">
                  {etaMinutes !== null ? etaMinutes : estimatedMinutes} mins
                </span>
              </div>
              <Progress value={progress} className="h-2" />
              <p className="text-xs text-muted-foreground text-right">
                {eta 
                  ? `Arriving at ${formatTime(eta)}`
                  : `Arriving at ${formatTime(booking.scheduled_at)}`
                }
              </p>
            </div>

            <Card className="overflow-hidden">
              {destination && (
                <DynamicMap
                  destination={destination}
                  filmmakerLocation={filmmakerLocation}
                  mode="customer"
                  height="256px"
                />
              )}
              {!destination && (
                <div ref={mapRef} className="w-full h-64 bg-muted" />
              )}
              <div className="p-3 text-center border-t">
                <Button variant="outline" className="w-full">
                  <Share2 className="h-4 w-4 mr-2" />
                  Share Tracking
                </Button>
              </div>
            </Card>

            {/* Filmmaker Profile Card - Right after map */}
            {booking.filmmaker && (
              <Card className="p-5 space-y-4">
                <div className="flex gap-4">
                  <div className="h-16 w-16 rounded-full bg-muted flex-shrink-0 overflow-hidden">
                    {booking.filmmaker.avatar_url ? (
                      <img 
                        src={booking.filmmaker.avatar_url} 
                        alt={booking.filmmaker.full_name || 'Filmmaker'}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center bg-primary/10 text-primary font-bold text-xl">
                        {booking.filmmaker.full_name?.charAt(0) || 'F'}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-bold text-lg truncate">{booking.filmmaker.full_name || 'Filmmaker'}</h3>
                      <span className="text-sm text-muted-foreground font-medium whitespace-nowrap ml-2">
                        {booking.status === 'en_route' ? 'On His Way' : 
                         booking.status === 'assigned' ? 'Assigned' : 
                         booking.status === 'arrived' ? 'Arrived' : 
                         booking.status === 'in_progress' ? 'In Progress' : 'Assigned'}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 mb-2">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <Star
                          key={i}
                          className={`h-4 w-4 ${
                            i <= Math.floor(Number(booking.filmmaker.rating || 5))
                              ? 'fill-red-500 text-red-500'
                              : 'text-muted-foreground'
                          }`}
                        />
                      ))}
                      <span className="text-sm ml-1 font-medium">
                        {Number(booking.filmmaker.rating || 5).toFixed(1)}/5.0
                      </span>
                    </div>
                    {booking.filmmaker.bio && (
                      <p className="text-sm text-muted-foreground line-clamp-3">
                        {booking.filmmaker.bio}
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" disabled>
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Chat with {booking.filmmaker.full_name?.split(' ')[0] || 'Filmmaker'}
                  </Button>
                  <Button variant="outline" className="flex-1" disabled>
                    <Phone className="h-4 w-4 mr-2" />
                    Call {booking.filmmaker.full_name?.split(' ')[0] || 'Filmmaker'}
                  </Button>
                </div>
              </Card>
            )}

          </>
        );

      case 'arrived':
        return (
          <>
            {/* Arrived - Verify Identity */}
            <Card className="p-5 space-y-4">
              <div className="flex gap-3">
                <div className="h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="h-6 w-6 text-destructive" />
                </div>
                                  <div className="flex-1">
                    <h2 className="font-bold text-lg mb-1">
                      Your Filmmaker has Arrived, You Can Confirm His Identity
                    </h2>
                    <p className="text-sm text-muted-foreground">Step 2 of 4</p>
                    <StepProgressBar currentStep={2} totalSteps={4} className="h-2 mt-2" />
                    <p className="text-xs text-muted-foreground mt-2">
                      The filmmaker will provide you with a 6-digit code. Enter it below to confirm their identity.
                    </p>
                  </div>
              </div>
            </Card>

            <Card className="p-8 space-y-6">
              <div className="text-center space-y-2">
                <h2 className="text-2xl font-bold">
                  {booking.filmmaker?.full_name} Has Arrived!
                </h2>
                <p className="text-sm text-muted-foreground">
                  Enter the verification code provided by your filmmaker
                </p>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center justify-center gap-3">
                  {verificationCode.map((digit, index) => (
                    <Input
                      key={index}
                      ref={(el) => (inputRefs.current[index] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleCodeInput(index, e.target.value)}
                      onKeyDown={(e) => handleCodeKeyDown(index, e)}
                      onPaste={handleCodePaste}
                      className={`w-16 h-20 text-center text-3xl font-bold border-2 transition-all ${
                        verificationError 
                          ? 'border-destructive bg-destructive/5' 
                          : digit 
                            ? 'border-primary bg-primary/5' 
                            : 'border-muted-foreground/30'
                      } rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20`}
                      autoFocus={index === 0}
                    />
                  ))}
                </div>

                {verificationError && (
                  <p className="text-sm text-center text-destructive font-medium">
                    {verificationError}
                  </p>
                )}
              </div>
            </Card>

          </>
        );

      case 'in_progress':
        return (
          <>
            {/* In Progress - Shooting */}
            <Card className="p-5 space-y-4">
              <div className="flex gap-3">
                <div className="h-12 w-12 rounded-full bg-orange-500/10 flex items-center justify-center flex-shrink-0">
                  <Clock className="h-6 w-6 text-orange-500" />
                </div>
                <div className="flex-1">
                  <h2 className="font-bold text-lg mb-1">
                    Your Shooting is in Progress...
                  </h2>
                  <p className="text-sm text-muted-foreground">Step 3 of 4</p>
                  <StepProgressBar currentStep={3} totalSteps={4} className="h-2 mt-2" />
                  <p className="text-xs text-muted-foreground mt-2">
                    {booking.filmmaker?.full_name} is shooting. Relax and enjoy the creative process!
                  </p>
                </div>
              </div>
            </Card>

            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-center">
                {booking.filmmaker?.full_name} is Shooting!
              </h2>
              <p className="text-center text-muted-foreground">
                You are free and encouraged to support him and his work. All our Filmmakers are trained in the highest standards to meet your expectations.
              </p>
              
              <div className="relative w-64 h-64 mx-auto">
                <svg className="w-full h-full transform -rotate-90">
                  <circle
                    cx="128"
                    cy="128"
                    r="110"
                    stroke="currentColor"
                    strokeWidth="8"
                    fill="none"
                    className="text-muted"
                  />
                  <circle
                    cx="128"
                    cy="128"
                    r="110"
                    stroke="currentColor"
                    strokeWidth="8"
                    fill="none"
                    strokeDasharray={`${2 * Math.PI * 110}`}
                    strokeDashoffset={`${2 * Math.PI * 110 * (1 - progress / 100)}`}
                    className="text-primary transition-all duration-1000"
                    strokeLinecap="round"
                  />
                </svg>
                
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div className="text-5xl font-bold">
                    {hours > 0 ? `${hours} Hour${hours > 1 ? 's' : ''}` : `${minutes} min`}
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    Estimated
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Shooting Time
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between text-sm text-muted-foreground px-8">
                <span>Started at {formatTime(booking.scheduled_at)}</span>
                <span>Finishing at {formatTime(estimatedEndTime)}</span>
              </div>
            </div>

            <div className="space-y-3">
              <Button 
                variant="outline"
                className="w-full h-12 text-base font-semibold"
              >
                Extend Time +
              </Button>


            </div>
          </>
        );

      case 'awaiting_finish_confirm':
        return (
          <>
            {/* Done - Confirm Completion */}
            <Card className="p-5 space-y-4">
              <div className="flex gap-3">
                <div className="h-12 w-12 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <div className="flex-1">
                  <h2 className="font-bold text-lg mb-1">
                    Confirm Shooting Completion
                  </h2>
                  <p className="text-sm text-muted-foreground">Step 4 of 4</p>
                  <StepProgressBar currentStep={4} totalSteps={4} className="h-2 mt-2" />
                  <p className="text-xs text-muted-foreground mt-2">
                    {booking.filmmaker?.full_name} has finished working. Confirm the service completion to receive your files and materials.
                  </p>
                </div>
              </div>
            </Card>

            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-center">Shooting is Done</h2>
              <p className="text-center text-muted-foreground">
                You are free and encouraged to support him and his work. All our Filmmakers are trained in the highest standards to meet your expectations.
              </p>
            </div>

            {booking.filmmaker && (
              <Card className="p-6 space-y-4">
                <div className="flex flex-col items-center text-center">
                  <div className="h-20 w-20 rounded-full bg-muted mb-4"></div>
                  
                  <div className="flex items-center gap-1 mb-2">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star
                        key={i}
                        className={`h-5 w-5 ${
                          i <= Math.floor(booking.filmmaker.rating)
                            ? 'fill-primary text-primary'
                            : 'text-muted-foreground'
                        }`}
                      />
                    ))}
                    <span className="text-sm ml-1 font-medium">{booking.filmmaker.rating}/5.0</span>
                  </div>
                  
                  <h3 className="text-xl font-bold mb-1">
                    {booking.filmmaker.full_name || 'Filmmaker'} has Finished
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Mark the service as completed below
                  </p>
                  
                  <div className="flex items-center justify-between w-full text-sm border-t border-b py-3">
                    <span className="text-muted-foreground">Started at {formatTime(booking.scheduled_at)}</span>
                    <span className="text-muted-foreground">Finished at {formatTime(estimatedEndTime)}</span>
                  </div>
                </div>
              </Card>
            )}

            <Card className="p-6 space-y-4">
              <h3 className="text-lg font-bold text-center">Give us a Tip</h3>
              
              <div className="grid grid-cols-3 gap-3">
                {tipOptions.map((option) => (
                  <Button
                    key={option.value}
                    variant={selectedTip === option.value ? "default" : "outline"}
                    className="h-16 text-lg font-bold"
                    onClick={() => setSelectedTip(option.value)}
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
              
              <Button
                variant="ghost"
                className="w-full"
                onClick={() => setSelectedTip(null)}
              >
                No Tip
              </Button>
            </Card>

            <div className="space-y-3">
              <Button 
                className="w-full h-14 text-base font-bold rounded-xl shadow-lg"
                onClick={handleMarkCompleted}
              >
                Mark the Service as Completed
              </Button>

              <Button 
                variant="outline"
                className="w-full h-12 text-base font-semibold"
              >
                Extend Time +
              </Button>

            </div>
          </>
        );

      case 'done_waiting_upload':
        return (
          <>
            {/* Content is being uploaded */}
            <Card className="p-5 space-y-4">
              <div className="flex gap-3">
                <div className="h-12 w-12 rounded-full bg-orange-500/10 flex items-center justify-center flex-shrink-0">
                  <Upload className="h-6 w-6 text-orange-500" />
                </div>
                <div className="flex-1">
                  <h2 className="font-bold text-lg mb-1">
                    Content is Being Uploaded
                  </h2>
                  <p className="text-sm text-muted-foreground">Step 4 of 4</p>
                  <StepProgressBar currentStep={4} totalSteps={4} className="h-2 mt-2" />
                  <p className="text-xs text-muted-foreground mt-2">
                    {booking.filmmaker?.full_name || 'Your Filmmaker'} has finished shooting. Please wait while they upload your footage.
                  </p>
                </div>
              </div>
            </Card>

            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-center">Shooting Complete</h2>
              <p className="text-center text-muted-foreground">
                {booking.filmmaker?.full_name} has finished your session. Your content will be available shortly.
              </p>
              
              <div className="relative w-64 h-64 mx-auto">
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div className="w-32 h-32 rounded-full bg-orange-500/10 flex items-center justify-center mb-4">
                    <Upload className="h-16 w-16 text-orange-500/50" />
                  </div>
                  <div className="text-lg font-semibold text-muted-foreground">
                    Awaiting Upload
                  </div>
                  <p className="text-sm text-muted-foreground mt-2 text-center">
                    We'll notify you when your content is ready
                  </p>
                </div>
              </div>
            </div>
          </>
        );

      case 'uploaded':
      case 'completed':
        return (
          <>
            {/* Uploaded/Completed - Download Content */}
            <div className="text-center space-y-4">
              <div className="w-24 h-24 mx-auto rounded-full bg-green-500/10 flex items-center justify-center">
                <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center">
                  <CheckCircle className="h-12 w-12 text-green-600" />
                </div>
              </div>
              
              <div>
                <h1 className="text-3xl font-bold mb-2">Shooting Done</h1>
                <p className="text-sm text-muted-foreground">
                  Finished {formatDate(booking.scheduled_at).split(',')[0]} at {formatTime(estimatedEndTime)}
                </p>
              </div>
            </div>

            <Card className="p-6 space-y-4">
              <h2 className="text-xl font-bold text-center">Your Content is Here</h2>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="relative aspect-video bg-muted rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Image className="h-12 w-12 text-muted-foreground" />
                  </div>
                  <div className="absolute bottom-2 left-2 bg-background/90 px-2 py-1 rounded text-xs font-semibold flex items-center gap-1">
                    <Camera className="h-3 w-3" />
                    View 13 images
                  </div>
                </div>
                
                <div className="relative aspect-video bg-muted rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Video className="h-12 w-12 text-muted-foreground" />
                  </div>
                  <div className="absolute bottom-2 left-2 bg-background/90 px-2 py-1 rounded text-xs font-semibold flex items-center gap-1">
                    <Video className="h-3 w-3" />
                    View 3 videos
                  </div>
                </div>
              </div>
            </Card>

            <div className="space-y-3">
              <Button 
                className="w-full h-14 text-base font-bold rounded-xl shadow-lg"
              >
                <Download className="h-5 w-5 mr-2" />
                Download All
              </Button>

              <Button 
                variant="outline"
                className="w-full h-12 text-base font-semibold"
                onClick={handleReview}
              >
                <Star className="h-5 w-5 mr-2" />
                Review Us
              </Button>
            </div>

            {booking.filmmaker && (
              <Card className="p-5 space-y-3">
                <div className="flex items-start gap-3">
                  <div className="h-12 w-12 rounded-full bg-muted flex-shrink-0"></div>
                  <div className="flex-1">
                    <h3 className="font-bold mb-1">A Note From {booking.filmmaker.full_name || 'Filmmaker'}</h3>
                    <p className="text-sm text-muted-foreground">
                      "Magnus tempus vestibulum gravida arcu. Tincidunt quam in ullamcorper risus facilisis commodo. Iaculis feugiat quam vulputate annean massa. Vulputate ut in fames velit. Quis nulla leo tristique ultrices. Mi amet aaculis posuere amet."
                    </p>
                  </div>
                </div>
              </Card>
            )}
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background pb-8">
      <header className="sticky top-0 z-10 bg-background border-b border-border px-4 py-3 flex items-center gap-3">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => navigate('/dashboard')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-lg font-semibold">
          Your {formatDate(booking.scheduled_at).split(',')[0]} Booking
        </h1>
      </header>

      <div className="px-4 py-6 space-y-6 max-w-lg mx-auto">
        {renderContent()}

        {/* Session Details - Collapsible */}
        <Collapsible open={detailsOpen} onOpenChange={setDetailsOpen}>
          <Card className="overflow-hidden">
            <CollapsibleTrigger className="w-full px-5 py-4 flex items-center justify-between hover:bg-muted/50 transition-colors">
              <h3 className="font-bold text-lg">Your Session Details</h3>
              <ChevronDown 
                className={`h-5 w-5 transition-transform ${detailsOpen ? 'rotate-180' : ''}`} 
              />
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="px-5 pb-5 space-y-3 border-t pt-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Camera className="h-5 w-5 text-primary" />
                    <span>Package</span>
                  </div>
                  <span className="font-medium text-right">{booking.package?.name}</span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Calendar className="h-5 w-5 text-primary" />
                    <span>Date</span>
                  </div>
                  <span className="font-medium text-right">
                    {formatDate(booking.scheduled_at)}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Clock className="h-5 w-5 text-primary" />
                    <span>Time</span>
                  </div>
                  <span className="font-medium text-right">
                    {formatTime(booking.scheduled_at)} - {formatTime(estimatedEndTime)}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <MapPin className="h-5 w-5 text-primary" />
                    <span>Location</span>
                  </div>
                  <span className="font-medium text-right max-w-[60%]">
                    {booking.address_line1}, {booking.city}, {booking.postal_code}
                  </span>
                </div>

                <div className="flex items-center justify-between border-t pt-3">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <DollarSign className="h-5 w-5 text-primary" />
                    <span className="font-semibold text-foreground">Total Amount</span>
                  </div>
                  <span className="font-bold text-lg">{formatPrice(booking.total_cents)}</span>
                </div>

                <div className="text-center pt-2">
                  <p className="text-xs text-muted-foreground">
                    Need Help?{" "}
                    <Button variant="link" className="p-0 h-auto text-xs">
                      Contact Our Human Support
                    </Button>
                  </p>
                </div>
              </div>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* What's Next - Collapsible */}
        {booking.status !== 'completed' && (
          <Collapsible open={whatsNextOpen} onOpenChange={setWhatsNextOpen}>
            <Card className="overflow-hidden">
              <CollapsibleTrigger className="w-full px-5 py-4 flex items-center justify-between hover:bg-muted/50 transition-colors">
                <h3 className="font-bold text-lg">What's Next?</h3>
                <ChevronDown 
                  className={`h-5 w-5 transition-transform ${whatsNextOpen ? 'rotate-180' : ''}`} 
                />
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="px-5 pb-5 space-y-4 border-t pt-4">
                  <div className="flex gap-3">
                    <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-primary-foreground">1</span>
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold mb-1">
                        Make Sure You are On-Site to Receive Your Dedicated Filmmaker
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-primary-foreground">2</span>
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold mb-1">
                        Prepare the Shooting Material and People Ahead for Smoother Experience
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-primary-foreground">3</span>
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold mb-1">
                        Share Your Wifi Credentials so the Filmmaker Can Upload the Material as Fast as Possible at the End of Your Shooting
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Don't worry, we do not store or share this information with anyone
                      </p>
                      <Button variant="link" className="p-0 h-auto text-xs mt-1">
                        Share Wifi
                      </Button>
                    </div>
                  </div>
                </div>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        )}

        {/* Cancel Booking */}
        {booking.status !== 'completed' && booking.status !== 'canceled' && (
          <Button 
            variant="ghost" 
            className="w-full h-12 text-base font-semibold text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={handleCancelBooking}
          >
            Cancel Booking
          </Button>
        )}
      </div>
    </div>
  );
}
